/**
 * Point 30 — "before sending quotation we required a client view of subjected quotation, without
 * downloading".
 *
 * What the customer will see, on screen, before it goes. Nothing is generated, nothing is fetched
 * and nothing is written: it renders the quotation already in hand, through a customer's eyes.
 *
 * ---- The whole point is what is MISSING ----
 *
 * Every internal number is stripped. No cost, no margin, no supplier, no rule violations, no
 * "source" column naming which supplier quotation a price came from. Those are the things a
 * purchaser must never accidentally show a customer, and a preview that leaks one is worse than no
 * preview at all — it teaches the purchaser that what they are looking at is safe.
 *
 * Deliberately no download and no print button (Akhil's ruling 2026-09-03: on-screen only). The
 * document that actually reaches the customer is generated server-side on send; this is a faithful
 * view of the same figures, not a second renderer competing to be the real one. It says so.
 */

import { Modal } from '../../components/Modal'
import { Button } from '../../components/ui'
import { formatMoney } from '../../lib/format'
import { computeQuotationTotals } from '../../lib/salesTaxes'
import type { Enquiry, Quotation, QuotationLine } from '../../lib/types'
import { inSerialOrder } from '../../lib/lineOrder'

/** Sell-side only. `cost_unit` is not read anywhere in this file, and that is the invariant. */
function lineTotal(line: QuotationLine) {
  return { amount: line.sell_unit.amount * line.qty, currency: line.sell_unit.currency }
}

export function QuotationPreviewModal({
  quotation,
  enquiry,
  onClose,
}: {
  quotation: Quotation
  enquiry: Enquiry
  onClose: () => void
}) {
  const header = enquiry.header
  const currency = quotation.totals.total.currency
  const { totals } = quotation
  // The same calculation the editor and the taxes grid run — one engine, so the customer's copy
  // cannot disagree with the screen the purchaser approved it on.
  const charges = computeQuotationTotals({
    netTotal: totals.total.amount,
    totalQty: quotation.lines.reduce((n, l) => n + l.qty, 0),
    taxes: quotation.taxes,
    discount:
      quotation.discount.pct > 0 || quotation.discount.amount > 0
        ? {
            applyOn: quotation.discount.apply_on,
            mode: quotation.discount.pct > 0 ? 'pct' : 'amount',
            value: quotation.discount.pct > 0 ? quotation.discount.pct : quotation.discount.amount,
          }
        : null,
  })

  const field = (label: string, value: string) =>
    value ? (
      <div style={{ display: 'flex', gap: 6, fontSize: 12.5 }}>
        <span style={{ color: 'var(--muted)', minWidth: 92 }}>{label}</span>
        <span style={{ fontWeight: 600 }}>{value}</span>
      </div>
    ) : null

  return (
    <Modal
      title="Client view — what the customer sees"
      onClose={onClose}
      width={860}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
          <span style={{ color: 'var(--muted)', fontSize: 11.5, flex: 1 }}>
            Preview only — nothing has been sent, and cost, margin and supplier are not shown here
            because the customer never sees them.
          </span>
          <Button onClick={onClose}>Close</Button>
        </div>
      }
    >
      {/* A sheet of paper, visually distinct from the working screen behind it, so nobody mistakes
          the preview for the editor and starts trying to type into it. */}
      <div style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 12, padding: '20px 22px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: 0.3 }}>QUOTATION</div>
            <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 2 }}>
              {quotation.id}
              {quotation.revision ? ` · Revision ${quotation.revision}` : null}
            </div>
          </div>
          <div style={{ display: 'grid', gap: 3, justifyItems: 'start' }}>
            {field('Date', header.date)}
            {field('Our ref', enquiry.ref)}
            {field('Currency', currency)}
          </div>
        </div>

        <div style={{ height: 1, background: 'var(--line)', margin: '14px 0' }} />

        <div style={{ display: 'grid', gap: 3, marginBottom: 14 }}>
          {/* The display label here, unlike everywhere else in this codebase: this block is FOR the
              customer, and a docname is not their name. Nothing is keyed on it. */}
          {field('To', quotation.customer_name || quotation.customer)}
          {/* The QUOTATION's own — editable there since 2026-09-15 — not the enquiry's. */}
          {field('Vessel', quotation.vessel || header.vessel || '')}
          {field('Delivery', quotation.delivery_location || header.delivery_location || '')}
          {field('Port', quotation.port || header.delivery_port || '')}
          {header.customer_reference_no ? field('Your ref', `${header.customer_reference_no}${header.customer_reference_date ? ` · ${header.customer_reference_date}` : ''}`) : null}
          {quotation.payment_terms_template ? field('Payment', quotation.payment_terms_template) : null}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 44 }}>#</th>
                <th>Item</th>
                <th style={{ width: 70, textAlign: 'right' }}>Qty</th>
                <th style={{ width: 70 }}>UOM</th>
                <th style={{ width: 110, textAlign: 'right' }}>Unit price</th>
                <th style={{ width: 120, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {/* The customer's own order, as the editor shows it (`QuoteLinesTable`): the server
                  appends a revision-added line LAST, so the stored order is not theirs. */}
              {inSerialOrder(quotation.lines).map((line) => (
                <tr key={line.line_id}>
                  {/* The CUSTOMER's own serial number, echoed back — it is how they will refer to
                      the line when they reply. Never our line id. */}
                  <td className="mono">{line.cust_sno}</td>
                  {/*
                    The NAME, not the code (Akhil, item 8): a customer reads "Oil filter 12mm",
                    not "1903010001". The description and the offer remark print under it, as
                    they do on the customer's copy; the code is kept small for cross-reference.
                  */}
                  <td>
                    <div style={{ fontWeight: 600 }}>{line.item_name || line.item_code}</div>
                    {line.description && line.description !== line.item_name ? (
                      <div style={{ fontSize: 12, whiteSpace: 'pre-line' }}>{line.description}</div>
                    ) : null}
                    {line.offer_remarks ? (
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', fontStyle: 'italic', whiteSpace: 'pre-line' }}>{line.offer_remarks}</div>
                    ) : null}
                    {line.item_name ? <div style={{ fontSize: 10.5, color: 'var(--muted)' }} className="mono">{line.item_code}</div> : null}
                  </td>
                  <td style={{ textAlign: 'right' }} className="mono">
                    {line.qty}
                  </td>
                  {/* What they will be billed in, which is not always what they asked in. */}
                  <td>{line.sell_uom || line.uom}</td>
                  <td style={{ textAlign: 'right' }} className="mono">
                    {formatMoney(line.sell_unit)}
                  </td>
                  <td style={{ textAlign: 'right' }} className="mono">
                    {formatMoney(lineTotal(line))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {quotation.lines.length === 0 ? (
          <div style={{ color: 'var(--warn)', fontSize: 12.5, padding: '10px 0' }}>
            ⚠ This quotation has no lines — the customer would receive an empty document.
          </div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <div style={{ minWidth: 280, display: 'grid', gap: 5 }}>
            <Row label="Subtotal" value={formatMoney(totals.sell_subtotal)} />
            {/*
              Shown only when non-zero. A customer-facing document listing "Convenience 0.00" invites
              a question about a charge that was never made — and these are exactly the lines a
              purchaser wants to eyeball before sending.
            */}
            {totals.commission_pct > 0 ? <Row label={`Commission ${totals.commission_pct}%`} value="" /> : null}
            {totals.convenience.amount > 0 ? <Row label="Convenience" value={formatMoney(totals.convenience)} /> : null}
            {/*
              Taxes and the discount, through the same engine the editor uses. The customer's copy
              MUST show what they will be charged — a preview whose total is the pre-tax figure is
              worse than no preview, because it looks authoritative.
            */}
            {charges.totalTax > 0 ? (
              <Row label="Taxes and charges" value={formatMoney({ amount: charges.totalTax, currency })} />
            ) : null}
            {charges.discountAmount > 0 ? (
              <Row label="Discount" value={`−${formatMoney({ amount: charges.discountAmount, currency })}`} />
            ) : null}
            <div style={{ height: 1, background: 'var(--line)', margin: '3px 0' }} />
            <Row label="Total" value={formatMoney({ amount: charges.grandTotal, currency })} strong />
          </div>
        </div>
        {/*
          The terms, as the customer will read them. `terms` is HTML from the template master —
          rendered here rather than shown as markup, because this preview's whole purpose is to be
          what they see. It is our own stored template text, not third-party input.
        */}
        {quotation.terms ? (
          <>
            <div style={{ height: 1, background: 'var(--line)', margin: '18px 0 12px' }} />
            <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 6 }}>Terms and conditions</div>
            <div
              className="termshtml"
              style={{ fontSize: 11.5, lineHeight: 1.55, color: 'var(--ink)' }}
              dangerouslySetInnerHTML={{ __html: quotation.terms }}
            />
          </>
        ) : null}
      </div>
    </Modal>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: strong ? 15 : 13 }}>
      <span style={{ color: strong ? 'var(--ink)' : 'var(--muted)', fontWeight: strong ? 800 : 400 }}>{label}</span>
      <span className="mono" style={{ fontWeight: strong ? 800 : 600 }}>
        {value}
      </span>
    </div>
  )
}
